/**
 * Unsaved form drafts (docs/FAILURES.md "401 keeps the form"). What someone types is copied to localStorage as they
 * type, per person and per form, so a session that expires mid-form (the app swaps to sign-in), a reload, or a crashed
 * screen doesn't lose it. After sign-in the same screen reads it back. A successful save clears it; signing out on
 * purpose clears all of that person's drafts. Drafts older than a day are dropped. Health answers are never drafted.
 */
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useAuthMaybe } from "../auth.tsx";

export const DRAFT_PREFIX = "cc.draft.";
export const DRAFT_MAX_AGE_MS = 24 * 3600_000;

const fullKey = (who: string, key: string) => `${DRAFT_PREFIX}${who.trim().toLowerCase()}.${key}`;

export const drafts = {
  get<T>(who: string, key: string, now = Date.now()): T | undefined {
    try {
      const raw = localStorage.getItem(fullKey(who, key));
      if (!raw) return undefined;
      const d = JSON.parse(raw) as { v: T; at: number };
      if (typeof d?.at !== "number" || now - d.at > DRAFT_MAX_AGE_MS) { localStorage.removeItem(fullKey(who, key)); return undefined; }
      return d.v;
    } catch { return undefined; }
  },
  set<T>(who: string, key: string, v: T, now = Date.now()): void {
    try { localStorage.setItem(fullKey(who, key), JSON.stringify({ v, at: now })); } catch { /* storage full or blocked: the form still works */ }
  },
  clear(who: string, key: string): void {
    try { localStorage.removeItem(fullKey(who, key)); } catch { /* ignore */ }
  },
};

/** Forget every draft of one person (sign out). */
export function clearDrafts(who: string): void {
  try {
    const prefix = `${DRAFT_PREFIX}${who.trim().toLowerCase()}.`;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k?.startsWith(prefix)) keys.push(k); }
    for (const k of keys) localStorage.removeItem(k);
  } catch { /* ignore */ }
}

/**
 * useState that survives a sign-in expiry. `key` undefined (or no signed-in person) = plain useState.
 * `isEmpty` says when the value is just the blank form, so an untouched form leaves no draft behind.
 */
export function useDraft<T>(key: string | undefined, initial: T | (() => T), isEmpty?: (v: T) => boolean): [T, Dispatch<SetStateAction<T>>, () => void] {
  const who = useAuthMaybe()?.user?.email;
  const active = key && who ? { who, key } : undefined;
  const [value, setValue] = useState<T>(() => {
    const saved = active ? drafts.get<T>(active.who, active.key) : undefined;
    return saved !== undefined ? saved : typeof initial === "function" ? (initial as () => T)() : initial;
  });
  useEffect(() => {
    if (!active) return;
    if (isEmpty?.(value)) drafts.clear(active.who, active.key);
    else drafts.set(active.who, active.key, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, active?.who, active?.key]);
  const clear = useCallback(() => {
    if (active) drafts.clear(active.who, active.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.who, active?.key]);
  return [value, setValue, clear];
}
